
import { UserService } from '../../core/service/user.service';
import { Component, OnInit, OnDestroy } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { Subscription } from 'rxjs';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { NGXLogger } from 'ngx-logger';
import { TextProcessor } from '../../core/utility/text.processor';
import { sumBy } from 'lodash';
import { TaskUtility } from 'src/app/task/task.utility';
import moment from 'moment';
import { AppConfigService } from 'src/app/core/service/app-config.service';
import { TranslateService } from '@ngx-translate/core';
import { MessengerService } from 'src/app/core/service/messenger.service';
import { saveAs } from '@progress/kendo-file-saver';
import { State } from '@progress/kendo-data-query';
import { TaskListService } from 'src/app/core/service/grid-data/grid-data-task.service';
import { ChannelService } from 'src/app/core/service/channel.service';
import { IntlService } from '@progress/kendo-angular-intl';
import { MessageHandlerService } from 'src/app/core/utility/message-handler.service';
import { PDFViewerTool } from '@progress/kendo-angular-pdfviewer';

/**
 * Angular component class for Channel details.
 */
@Component({
  selector: 'app-channel-detail',
  templateUrl: './channel-detail.component.html',
  styleUrls: ['./channel-detail.component.scss']
})
export class ChannelDetailComponent implements OnInit, OnDestroy
{
  /**
   * Channel ID
   */
  public id: number;

  /**
   * Data model object for this component.
   */
  public channel: any = {};

  /**
   * Tasks statistics.
   */
  public taskStat: any = {};

  /**
   * Data array for Tasks pie chart.
   */
  public taskPieData: any[] = [];

  /**
   * Channel staff map. The key is user name.
   */
  public staff: Map<string, any> = new Map();

  /**
   * List of unique channel staff usernames.
   */
  public staffUserNames: string[];

  /**
   * The currently selected staff member.
   */
  public staffUserSelected: any = null;

  /**
   * Photos of staff. The key is user name.
   */
  public photoMap: Map<string, string> = new Map();

  /**
   * Kendo GridView for Tasks.
   */
  public gridView: GridDataResult;

  /**
   * Channel Goal statistics data.
   */
  public goalData = [];

  /**
   * Channel goal loading.
   */
  public goalLoading = false;

  /**
   * Channel history entries.
   */
  public historyEntries: any[] = [];

  /**
   * Channel history object.
   */
  public historyData: any = {};

  /**
   * Channel history loading.
   */
  public historyLoading = false;

  /**
   * Controls the task assignee dialog.
   */
  public assigneeDialogOpened = false;

  /**
   * Controls the task duedate dialog.
   */
  public taskDueDateOpened = false;

  /**
   * Controls the report dialog.
   */
  public reportDialogOpened = false;

  /**
   * Selected task for re-assignment.
   */
  public task = null;

  /**
   * Text shorted of the taskname
   */
  public shortTaskName: string;
  public loadingDocument = false;
  public documentContent: string;
  public documentName: string;
  public taskCount = 0;
  public pageSizes = [5, 10, 25, 50, 100];
  public pdfViewerTools: PDFViewerTool[] = [
    /*Disable search for because it a bit slow with pdf and multiple pages
    'search',*/
    'selection',
    'spacer',
    'zoom',
    'zoomInOut',
    'spacer',
    'download',
    'print',
    'pager',
  ];

  /**
   * Task list Kendo grid state.
   */
  public state: State = {
    skip: 0,
    // Initial filter descriptor
    filter: {
      logic: 'and',
      filters: []
    }
  };

  public loadingCounts = true;

  /**
   * Subscriptions
   */
  private listSubscription: Subscription;
  private routeParamSubscription: Subscription;
  private messagingSubscription: Subscription;

  /**
   * Localization texts
   */
  private metGoalText: string;
  private missedGoalText: string;
  private unassignedText: string;

  // color constants.
  private readonly good = '#669146';
  private readonly bad = '#cf4526';
  private readonly maybe = '#e07a1a';
  private readonly neutral = '#000000';
  private readonly darkGreen = '#53a5a1';

  /**
   * Days of the week
   */
  private days = [];

  /**
   * Constructor
   *
   * @param route Auto injected ActivatedRoute
   */
  constructor(private route: ActivatedRoute,
    private channelService: ChannelService,
    private userService: UserService,
    private logger: NGXLogger,
    private appConfigService: AppConfigService,
    private translate: TranslateService,
    private messengerService: MessengerService,
    private taskListService: TaskListService,
    private taskUtilityService: TaskUtility,
    private messageHandlerService: MessageHandlerService,
    public intl: IntlService)
  {
    // subscribe to route parameter changes.
    this.routeParamSubscription = this.route.params.subscribe({
      next: (params: Params) =>
      {
        this.id = +params.id;
      }
    });
  }

  public get loading(): boolean
  {
    return this.taskListService.loading === undefined || this.taskListService.loading;
  }

  /**
   * Calculates the integral value of a percentage.
   *
   * @param x percentage real value
   */
  public percentageInt(x: number): number
  {
    return Math.round(x * 100);
  }

  public ngOnInit(): void
  {
    this.translate.get('DAYS').subscribe({
      next: (res) =>
      {
        const splitted = res.split(',');
        for (let i = 0; i < splitted.length; i++)
        {
          this.days.push(splitted[i]);
        }
      }
    });

    this.listSubscription = this.taskListService.subscribe({
      next: (value) =>
      {
        if (value)
        {
          value.data.forEach(d =>
          {
            d.openedOn = new Date(d.openedOn);
            if (d.dueDate)
            {
              d.dueDate = new Date(d.dueDate);
              d = this.setRelatedProperties(d);
            }
            d.priorityBadgeClass = this.taskUtilityService.priorityBadgeClass(d.priority);
            d.priority = this.taskUtilityService.priority(d.priority);
          });

          this.taskCount = value.total;
          this.gridView = { data: value.data, total: value.total };

          this.loadTaskSummary();
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });

    this.messagingSubscription = this.messengerService.receiveMessage().subscribe({
      next: (message) =>
      {
        if (message.type === 'taskHistoryUpdated' || message.type === 'taskDueDateChanged')
        {
          this.loadGoal();
        }
      }
    });

    // Get the translation used when file types is invalid
    this.translate.get(['CHANNEL-SUMMARY-REPORT-DOCUMENT', 'MET-GOAL', 'MISSED-GOAL', 'UNASSIGNED']).subscribe({
      next: (res) =>
      {
        this.documentName = res['CHANNEL-SUMMARY-REPORT-DOCUMENT'];
        this.metGoalText = res['MET-GOAL'];
        this.missedGoalText = res['MISSED-GOAL'];
        this.unassignedText = res.UNASSIGNED;

        this.loadGoal();
      }
    });
  }

  public ngOnDestroy(): void
  {
    // unsubscribe to route parameter changes.
    if (this.routeParamSubscription !== undefined) { this.routeParamSubscription.unsubscribe(); }
    if (this.messagingSubscription !== undefined) { this.messagingSubscription.unsubscribe(); }
    if (this.listSubscription !== undefined) { this.listSubscription.unsubscribe(); }
  }

  /**
   * Data change event handler.
   *
   * @param state State of grid operations
   */
  public dataStateChange(state: DataStateChangeEvent): void
  {
    this.setStateAndQuery(state);
  }

  /**
   * Filter the array of tasks by assignee.
   *
   * @param user The task assignee.
   */
  public filterTasksByUser(user): void
  {
    if (this.staffUserSelected !== user)
    {
      user.selected = true;
      if (this.staffUserSelected !== null)
      {
        this.staffUserSelected.selected = false;
      }
      this.staffUserSelected = user;
      this.setStateAndQuery();
    }
  }

  /**
   * De-select a staff member
   */
  public deselectUser(): void
  {
    if (this.staffUserSelected !== null)
    {
      this.staffUserSelected.selected = false;
      this.staffUserSelected = null;
      this.setStateAndQuery();
    }
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskAssigneeDialog(task): void
  {
    this.task = task;
    this.shortTaskName = TextProcessor.truncateText(this.task.name, 35);
    this.assigneeDialogOpened = true;
  }

  /**
   * Sets the selected task, and opens the task assignment dialog modal.
   *
   * @param task Selected task for re-assignment.
   */
  public openTaskDuedateDialog(task): void
  {
    this.task = task;
    this.taskDueDateOpened = true;
  }

  /**
   * Sets the selected task to null, and closes the task assignment dialog modal.
   */
  public closeTaskAssigneeDialog(): void
  {
    this.task = null;
    this.assigneeDialogOpened = false;
  }

  /**
   * Updates a task's assignee.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskAssignee(assignee): void
  {
    this.task.assigneeName = assignee.name;
    this.task.assignee = assignee.username;
    this.closeTaskAssigneeDialog();
  }

  /**
   * Updates a task's due date.
   *
   * @param task Selected task for re-assignment.
   * @param assigneeForm Form of the task-assign component.
   */
  public updateTaskDueDate(task): void
  {
    this.task.dueDate = task.dueDate;
    this.task = this.setRelatedProperties(this.task);
    this.taskDueDateOpened = false;
  }

  public closeTaskDueDateDialog(): void
  {
    this.task = null;
    this.taskDueDateOpened = false;
  }

  /**
   * Get the user photo.
   *
   * @param userName Username
   */
  public getPhotoFromService(userName: string, userFullName: string): void
  {
    this.userService.getCachedPhotoUser(userName).subscribe({
      next: (data) =>
      {
        if (data.imageContent !== null)
        {
          const base64 = data.imageContent;
          this.photoMap.set(userName, 'data:image/jpeg;base64,' + base64);
        }
        else
        {
          const splitted = userFullName.split(' ');
          this.photoMap.set(userName, (splitted.length >= 2) ? `${splitted[0].charAt(0)}${splitted[1].charAt(0)}` : null);
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Download Function for DEMO
   *
   */
  public downloadReport(): void
  {
    document.body.style.overflow = 'hidden';
    this.reportDialogOpened = true;
    this.loadingDocument = true;
    this.channelService.generateReport(this.channel.id).subscribe({
      next: (data) =>
      {
        if (data === null)
        {
          this.closeDocumentViewerDialog();
        }
        else
        {
          this.loadingDocument = false;
          this.documentContent = `data:application/pdf;base64,${data.content}`;
          saveAs(`data: ${data.mimeType}; base64, ${data.content} `, this.documentName);
        }
      },
      error: (error) =>
      {
        this.logger.error(error);
        this.messageHandlerService.displayError(error);
        this.closeDocumentViewerDialog();
      }
    });
  }

  /**
   * Close the document viewer modal
   */
  public closeDocumentViewerDialog(): void
  {
    this.reportDialogOpened = false;
    document.body.style.overflow = 'auto';
  }

  public clickOutside(): void
  {
    if (!this.assigneeDialogOpened && !this.taskDueDateOpened && !this.reportDialogOpened)
    {
      this.deselectUser();
    }
  }

  private setRelatedProperties(d: any): any
  {
    const now = new Date();
    const d1 = moment(Date.UTC(d.dueDate.getFullYear(), d.dueDate.getMonth(), d.dueDate.getDate()));
    const d2 = moment(Date.UTC(now.getFullYear(), now.getMonth(), now.getDate()));
    d.daysLeft = d1.diff(d2, 'days');

    d.statusIcon = `fas fa-2x ${d.daysLeft > 1 ? 'fa-clipboard-check icon-success'
      : d.daysLeft > 0 ? 'fa-alarm-clock icon-warn' : 'fa-exclamation-triangle icon-danger'}`;
    d.statusTitle = d.daysLeft > 1 ? 'PROGRAM-STATUS-ON-TRACK' :
      d.daysLeft > 0 ? 'PROGRAM-STATUS-DUE-SOON' : 'PROGRAM-STATUS-OVERDUE';

    return d;
  }

  /**
   * Load Channel History
   */
  private loadHistory(): void
  {
    this.historyLoading = true;

    this.channelService.getHistory(this.id).subscribe({
      next: (data) =>
      {
        if (data.length > 0) // if there are history data
        {
          data.forEach(d =>
          {
            d.date = moment(d.date).toDate();
            d.day = this.days[d.date.getDay()].charAt(0);
          });

          this.historyEntries = data;
          this.historyData.caseOpenedSeries = data.map(d => d.casesOpened);
          this.historyData.caseClosedSeries = data.map(d => d.casesClosed);
          this.historyData.weekdaySeries = data.map(d => d.day);
          this.historyData.yMax = Math.max(...this.historyData.caseOpenedSeries.concat(this.historyData.caseClosedSeries)) + 5;
          this.historyData.yStep = Math.round(this.historyData.yMax / 2);
        }
        this.channelService.getStatistics(this.id).subscribe({
          next: (d) =>
          {
            this.historyData.closureRate = d.rate;
            this.channel.statistics.openCases = d.openCases;
          },
          error: (errorResponse: any) =>
          {
            this.messageHandlerService.displayError(errorResponse);
          }
        });
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.historyLoading = false;
        this.loadTasks();
      }
    });
  }

  /**
   * Load Tasks Summary for the channel and different pieces of graphics data.
   */
  private loadTaskSummary(): void
  {
    this.loadingCounts = true;
    this.channelService.getTasksSummary(this.id).subscribe({
      next: (data) =>
      {
        // Reset in case of reload after save
        this.staff.forEach(user =>
        {
          user.open = 0;
          user.late = 0;
        });

        const totalTasks = sumBy(data, (o) => o.open);
        const lateTasks = sumBy(data, (o) => o.late);

        this.taskStat = {
          open: totalTasks,
          late: lateTasks
        };

        const onTimeTasks = this.taskStat.open - this.taskStat.late;
        this.taskPieData = [
          { value: lateTasks, category: 'Late', color: this.bad, percentage: lateTasks / totalTasks },
          { value: onTimeTasks, category: 'On Time', color: this.darkGreen, percentage: onTimeTasks / totalTasks }
        ];

        this.staff.forEach((v, k) =>
        {
          const countSummary = data.find(x => x.assignee === k);
          if (countSummary !== undefined)
          {
            v.open = countSummary.open;
            v.late = countSummary.late;
          }
          v.onTime = v.open - v.late;
          v.taskPieData = [
            { value: v.late, category: 'Late', color: this.bad, percentage: v.late / v.open },
            { value: v.onTime, category: 'On Time', color: this.darkGreen, percentage: v.onTime / v.open }
          ];
        });

        // Add any staff member is not in the map yet
        data.forEach(d =>
        {
          if (!this.staff.has(d.assignee))
          {
            const onTime = d.open - d.late;
            this.staff.set(d.assignee,
              {
                userName: d.assignee,
                name: d.assignee === 'Unassigned' ? this.unassignedText : d.assigneeName,
                open: d.open,
                late: d.late,
                onTime: d.open - d.late,
                selected: false,
                taskPieData: [
                  { value: d.late, category: 'Late', color: this.bad, percentage: d.late / d.open },
                  { value: onTime, category: 'On Time', color: this.darkGreen, percentage: onTime / d.open }
                ]
              });
          }

          if (d.assigneeRole === 'ROLE_COMMUNITY_USER' && d.assignee)
          {
            this.getPhotoFromService(d.assignee, d);
          }
        });

        // Removed Unassigned automatically if there is no more open tasks
        if (this.staff.has('Unassigned') && this.staff.get('Unassigned').open === 0)
        {
          this.staff.delete('Unassigned');
          if (this.staffUserSelected !== null && this.staffUserSelected.userName === 'Unassigned')
          {
            this.deselectUser();
          }
        }

        // Must set the initials for users not part of the Channels
        this.staff.forEach(staff =>
        {
          if (!this.photoMap.get(staff.userName))
          {
            const splitted = staff.name.split(' ');
            this.photoMap.set(staff.userName, (splitted.length >= 2) ? `${splitted[0].charAt(0)}${splitted[1].charAt(0)}` : null);
          }
        });

        // Update staffUsersNames array
        this.staffUserNames = Array.from(this.staff.keys());
        this.loadingCounts = false;
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      }
    });
  }

  /**
   * Load Goal
   */
  private loadGoal(): void
  {
    this.goalLoading = true;

    // get Channel by ID from REST service
    this.channelService.getChannel(this.id).subscribe({
      next: (data) =>
      {
        this.channel = data;
        const stat = this.channel.statistics;
        if (stat !== null)
        {
          this.channel.goalPercentage = stat.casesMetGoal / (stat.casesMetGoal + stat.casesFailedGoal);
          this.channel.goalPercentageInteger = Math.round(this.channel.goalPercentage * 100);

          const metGoal = this.metGoalText.replace('{{value}}', this.channel.goalPercentageInteger);
          const missedGoal = this.missedGoalText.replace('{{value}}', (100 - this.channel.goalPercentageInteger).toString());
          this.goalData = [
            {
              category: metGoal,
              value: this.channel.goalPercentage,
              color: stat.targetDaysToCloseCase > 0 ?
                (this.channel.goalPercentage > 0.9 ? this.good : this.channel.goalPercentage > 0.7 ? this.maybe : this.bad)
                : this.neutral
            },
            {
              category: missedGoal,
              value: 1 - this.channel.goalPercentage,
              color: 'rgba(0, 0, 0, 0.05)'
            }
          ];

          if (stat.targetDaysToCloseCase > 0)
          {
            this.channel.statusClass = stat.averageDaysToCloseCase > stat.targetDaysToCloseCase ?
              'bad' : stat.averageDaysToCloseCase >= stat.targetDaysToCloseCase * 0.9 ? 'maybe' : 'good';
          }
          else
          {
            this.channel.statusClass = 'neutral';
          }
        }

        if (this.channel.id !== null)
        {
          this.userService.getUsersByChannelGroups(this.channel.id).subscribe({
            next: (users) =>
            {
              users.forEach(user =>
              {
                if (user.enabled)
                {
                  this.staff.set(user.username,
                    {
                      userName: user.username,
                      name: user.name,
                      tasks: [],
                      open: 0,
                      late: 0,
                      selected: false
                    });
                  this.getPhotoFromService(user.username, user.name);
                }
              });
              if (!this.staff.has(this.channel.leaderUserName))
              {
                this.staff.set(this.channel.leaderUserName,
                  {
                    userName: this.channel.leaderUserName,
                    name: this.channel.leader,
                    tasks: [],
                    open: 0,
                    late: 0,
                    selected: false
                  });
                this.getPhotoFromService(this.channel.leaderUserName, this.channel.leader);
              }
            }
          });
        }
      },
      error: (errorResponse: any) =>
      {
        this.messageHandlerService.displayError(errorResponse);
      },
      complete: () =>
      {
        this.goalLoading = false;
        this.loadHistory();
      }
    });
  }

  /**
   * Load Channel Tasks
   */
  private loadTasks(): void
  {
    this.setStateAndQuery();
  }

  private setStateAndQuery(newState: DataStateChangeEvent = null): void
  {
    // Initialization of state defaults
    this.state = {
      filter: {
        filters: [
          { field: 'channelId', operator: 'eq', value: this.id },
          { field: 'closedOn', operator: 'isnull', value: '' },
        ], logic: 'and'
      },
      group: [],
      skip: 0,
      sort: [],
      take: this.appConfigService.getConfig().pageSize
    };

    // Set new state on dataStateChange
    if (newState)
    {
      this.state.skip = newState.skip;
      this.state.sort = newState.sort;
      this.state.take = newState.take;
    }

    // Apply filter if filtered by assignee
    if (this.staffUserSelected)
    {
      // If userName is Unassigned, then we must check for null assignee
      if (this.staffUserSelected.userName === 'Unassigned')
      {
        this.state.filter.filters.push({ field: 'assigneeName', operator: 'isnull', value: '' });
      }
      else
      {
        this.state.filter.filters.push({ field: 'assigneeName', operator: 'eq', value: this.staffUserSelected.name });
      }
    }
    this.taskListService.queryByState(this.state);
  }
}
